import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportDataService } from 'app/core/services/import-data.service';
import { PrivateService } from 'app/core/services/private.service';
import { concatMap, delay, from, of, tap } from 'rxjs';

@Component({
  selector: 'modal-import-charge',
  templateUrl: './modal-import-charge.component.html',
  styleUrls: ['./modal-import-charge.component.scss']
})
export class ModalImportChargeComponent implements OnInit, AfterViewInit {

  isLoading: boolean = false;
  formatError: boolean = false;

  insertionMessage: string = null;
  totalCharges: number = 0;
  isInserting = false;
  displayTable = true;
  isImported = false;

  @Input() importedData: any[] = [];
  selectedCharges: any[] = [];
  dataSource = new MatTableDataSource<any>(this.importedData);

  displayedColumns: string[] = [
    'select', 'name', 'taxCategory', 'computePagibig', 'computePhilhealth',
    'computeSSS', 'employeeShare', 'subjectToTax'
  ];

  expectedHeaders: string[] = [
    'name', 'taxCategory', 'computePagibig', 'computePhilhealth',
    'computeSSS', 'employeeShare', 'subjectToTax'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(public activeModal: NgbActiveModal, private http: HttpClient, private importDataService: ImportDataService) { }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedCharges = [...this.importedData];

    if (!this.isDataFormatCorrect()) {
      this.formatError = true;
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  isDataFormatCorrect(): boolean {
    if (this.importedData.length === 0) return true;
    const importedHeaders = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(header => importedHeaders.includes(header));
  }

  onCheckboxChange(charge: any, isChecked: boolean): void {
    if (isChecked) {
      this.selectedCharges.push(charge);
    } else {
      const index = this.selectedCharges.indexOf(charge);
      if (index !== -1) {
        this.selectedCharges.splice(index, 1);
      }
    }
  }

  isSelected(charge: any): boolean {
    return this.selectedCharges.includes(charge);
  }

  toggleSelectAll(isChecked: boolean): void {
    if (isChecked) {
      this.selectedCharges = [...this.importedData];
    } else {
      this.selectedCharges = [];
    }
  }
  
  saveSelectedCharges(): void {
    this.totalCharges = this.selectedCharges.length;
    let insertedCount = 0;
    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;

    this.insertionMessage = "Please wait..."

    const convertedCharges = this.selectedCharges.map(charge => ({
      ...charge
    }));

    const chargeInsertions$ = from(convertedCharges).pipe(
      concatMap((charge) =>
        of(charge).pipe(
          delay(3000),
          concatMap(emp =>
            this.importDataService.insertChargeDetailsFromCSV(emp).pipe(
              tap({
                next: () => {
                  insertedCount++;
                  this.insertionMessage = `Inserting charge details ${insertedCount} of ${this.totalCharges}, please wait...`;
                },
                error: (err) => {
                  console.error(err);
                  this.insertionMessage = `Error inserting details for Charge ID ${emp.chargeId}. Skipping...`;
                }
              })
            )
          )
        )
      )
    );

    chargeInsertions$.subscribe({
      next: () => {
        // This will run for each successful insertion
      },
      error: (err) => {
        console.error('An error occurred during the insertion process:', err);
        this.insertionMessage = `Error inserting charge details. Please try again.`;
      },
      complete: () => {
        // All insertions are done
        if (insertedCount === this.totalCharges) {
          this.insertionMessage = `All ${this.totalCharges} charge details inserted successfully.`;
          this.isImported = true;
        } else {
          this.insertionMessage += ` ${this.totalCharges - insertedCount} insertion(s) failed.`;
        }
        this.isInserting = false;
        this.isLoading = false;
      }
    });
  }

  downloadTemplate(): void {
    const csvFilePath = 'assets/csv/Charge_Data_Template.csv';

    this.http.get(csvFilePath, { responseType: 'blob' }).subscribe((data: Blob) => {
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'Charge_Data_Template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  onClose(): void {
    this.activeModal.dismiss('cancel');
    window.location.reload();
  }
}
